import { Injectable, signal, computed } from '@angular/core';
import * as THREE from 'three';

export enum InteractionModeEnum {
  NONE,
  EDGE,
  JAMB,
  CUTAWAY,
  SPLASHBACK,
  SNAPSHOT,
  CUTTING
}

export interface SelectionState {
  // Raw Interactions (Physics)
  lastRaycast: THREE.Intersection[] | null;
  lastPointerPosition: THREE.Vector2 | null;

  // Logical Selection (Business)
  selectedPlanId: number | null;
  selectedGroupId: string | null;

  // Specific Sub-selection (Edges, etc.)
  activeMode: InteractionModeEnum;

  // Hover State
  hoveredPlanId: number | null;
}

@Injectable({
  providedIn: 'root' // Singleton Store
})
export class WorkbenchSelectionStore {

  // --- Signals ---

  // Physics Layer
  readonly rawIntersections = signal<THREE.Intersection[]>([]);
  readonly pointerPosition = signal<THREE.Vector2>(new THREE.Vector2());

  // Logic Layer
  readonly selectedPlanId = signal<number | null>(null);
  readonly selectedGroupId = signal<string | null>(null);
  readonly activeMode = signal<InteractionModeEnum>(InteractionModeEnum.NONE);

  // Computed (Derived State)
  readonly hasSelection = computed(() => this.selectedPlanId() !== null);

  // Actions

  public setIntersections(hits: THREE.Intersection[]) {
    this.rawIntersections.set(hits);
  }

  public setPointer(pos: THREE.Vector2) {
    this.pointerPosition.set(pos.clone());
  }

  public selectPlan(planId: number | null) {
    this.selectedPlanId.set(planId);
    // If selecting null, also clear group? Or keep group context? 
    // Usually standard behavior is clear all.
    if (planId === null) {
      this.selectedGroupId.set(null);
    }
  }

  public selectGroup(groupId: string | null) {
    this.selectedGroupId.set(groupId);
  }

  public setMode(mode: InteractionModeEnum) {
    this.activeMode.set(mode);
  }

  public reset() {
    this.selectedPlanId.set(null);
    this.selectedGroupId.set(null);
    this.activeMode.set(InteractionModeEnum.NONE);
    this.rawIntersections.set([]);
  }
}
